/**
 * TabReturnModal.tsx
 * Warning modal when user returns to exam tab
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { Card } from '@vsat/shared/components';

interface Props {
  isTabActive: boolean;
  violationCount: number;
  onAcknowledge: () => void;
}

export default function TabReturnModal({ isTabActive, violationCount, onAcknowledge }: Props) {
  const [showModal, setShowModal] = useState(false);
  const [wasInactive, setWasInactive] = useState(false);

  useEffect(() => {
    if (!isTabActive) {
      setWasInactive(true);
    } else if (wasInactive && isTabActive) {
      setShowModal(true);
      setWasInactive(false);
    }
  }, [isTabActive, wasInactive]);

  const handleAcknowledge = () => {
    setShowModal(false);
    onAcknowledge();
  };

  if (!showModal) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
      <Card className='w-full max-w-md mx-4 p-6 shadow-xl border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 backdrop-blur-sm rounded-2xl group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20'>
        <div className='flex items-start gap-4'>
          <div className='flex-shrink-0 w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/25'>
            <AlertTriangle className='w-5 h-5 text-white' />
          </div>

          <div className='flex-1'>
            <h3 className='text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2'>
              Tab Switch Detected
            </h3>

            <p className='text-amber-800 dark:text-amber-200 text-sm mb-4'>
              You left the exam tab. This activity has been recorded as a security violation.
            </p>

            <div className='bg-amber-100 dark:bg-amber-900/30 rounded-lg p-3 mb-4 border border-amber-200 dark:border-amber-800'>
              <p className='text-xs text-amber-700 dark:text-amber-300'>
                <strong>Current violations:</strong> {violationCount}/3
              </p>
              <p className='text-xs text-amber-700 dark:text-amber-300 mt-1'>
                Reaching 3 violations will result in automatic exam submission.
              </p>
            </div>

            <div className='flex justify-end gap-2'>
              <Button
                onClick={handleAcknowledge}
                className='bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white font-semibold shadow-lg shadow-primary/25 transition-all duration-300'
              >
                I Understand
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
